import axiosInstance from '../lib/axios';
import { Project, ProjectTeamMember, Task, CreateProjectRequest, UpdateProjectRequest, ProjectFilters, ProjectsResponse } from '../types/models';
import { getSecureJSON } from '../utils/localStorage';

class ProjectService {
  private baseUrl = '/projects';

  // Helper to check if user is Super Admin
  private isSuperAdmin(): boolean {
    try {
      const authData = getSecureJSON('auth-storage');
      const userRole = authData?.state?.user?.role;
      return userRole === 'Super Admin' || userRole === 'SUPER_ADMIN' || userRole === 'super_admin';
    } catch {
      return false;
    }
  }

  // GET all projects with filters
  async getProjects(filters: ProjectFilters = {}): Promise<ProjectsResponse> {
    try {
      const params = new URLSearchParams();
      
      // Only add myProjects=true for non-Super Admin users
      if (!this.isSuperAdmin()) {
        params.append('myProjects', 'true');
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          const stringValue = typeof value === 'number' ? value.toString() : value;
          if (stringValue && stringValue.trim() !== '') {
            params.append(key, stringValue);
          }
        }
      });
      const queryString = params.toString();
      const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;
      const response = await axiosInstance.get<ProjectsResponse>(url);
      
      if (response.data && Array.isArray(response.data.data)) {
        return response.data;
      } else if (Array.isArray(response.data)) {
        // fallback if API returns array directly
        return { 
          data: response.data, 
          total: response.data.length, 
          page: 1, 
          limit: response.data.length, 
          hasMore: false 
        };
      } else {
        throw new Error('Unexpected response structure for getProjects');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  // GET project by ID
  async getProjectById(id: string): Promise<Project> {
    try {
      const response = await axiosInstance.get<Project>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching project ${id}:`, error);
      throw error;
    }
  }

  // CREATE new project
  async createProject(projectData: CreateProjectRequest): Promise<Project> {
    try {
      const response = await axiosInstance.post<Project>(this.baseUrl, projectData);
      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  // UPDATE project
  async updateProject(id: string, projectData: UpdateProjectRequest): Promise<Project> {
    try {
      const response = await axiosInstance.patch<Project>(`${this.baseUrl}/${id}`, projectData);
      return response.data;
    } catch (error) {
      console.error(`Error updating project ${id}:`, error);
      throw error;
    }
  }

  // DELETE project
  async deleteProject(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error(`Error deleting project ${id}:`, error);
      throw error;
    }
  }

  // GET project team members
  async getProjectTeamMembers(projectId: string): Promise<ProjectTeamMember[]> {
    try {
      const response = await axiosInstance.get<ProjectTeamMember[]>(`${this.baseUrl}/${projectId}/team`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching team members for project ${projectId}:`, error);
      throw error;
    }
  }

  // ADD team member to project
  async addTeamMember(projectId: string, teamMember: Omit<ProjectTeamMember, 'id'>): Promise<ProjectTeamMember> {
    try {
      const response = await axiosInstance.post<ProjectTeamMember>(`${this.baseUrl}/${projectId}/team`, teamMember);
      return response.data;
    } catch (error) {
      console.error(`Error adding team member to project ${projectId}:`, error);
      throw error;
    }
  }

  // REMOVE team member from project
  async removeTeamMember(projectId: string, employeeId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseUrl}/${projectId}/team/${employeeId}`);
    } catch (error) {
      console.error(`Error removing team member from project ${projectId}:`, error);
      throw error;
    }
  }

  // GET project tasks
  async getProjectTasks(projectId: string): Promise<Task[]> {
    try {
      const response = await axiosInstance.get<Task[]>(`${this.baseUrl}/${projectId}/tasks`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tasks for project ${projectId}:`, error);
      throw error;
    }
  }

  // GET project statistics
  async getProjectStats(projectId: string): Promise<{
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    blockedTasks: number;
    totalHours: number;
    teamSize: number;
  }> {
    try {
      const response = await axiosInstance.get(`${this.baseUrl}/${projectId}/stats`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching stats for project ${projectId}:`, error);
      throw error;
    }
  }

  // UPDATE project status
  async updateProjectStatus(projectId: string, status: Project['status']): Promise<Project> {
    try {
      const response = await axiosInstance.patch<Project>(`${this.baseUrl}/${projectId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating project status ${projectId}:`, error);
      throw error;
    }
  }

  // GET projects by client
  async getProjectsByClient(clientId: string): Promise<Project[]> {
    try {
      const myProjectsParam = this.isSuperAdmin() ? '' : '?myProjects=true';
      const response = await axiosInstance.get<Project[]>(`${this.baseUrl}/client/${clientId}${myProjectsParam}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching projects for client ${clientId}:`, error);
      throw error;
    }
  }

  // GET projects by manager
  async getProjectsByManager(managerId: string): Promise<Project[]> {
    try {
      const myProjectsParam = this.isSuperAdmin() ? '' : '?myProjects=true';
      const response = await axiosInstance.get<Project[]>(`${this.baseUrl}/manager/${managerId}${myProjectsParam}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching projects for manager ${managerId}:`, error);
      throw error;
    }
  }
}

export const projectService = new ProjectService();
export default projectService;